/**
 * Shared fund calculation constants and utilities.
 *
 * Single source of truth for capital structure, waterfall splits, fee rates,
 * and simulation logic used by PEDashboard and CashRequirements.
 */

// Capital structure: Participant 15%, EQT Perf 35%, SEB 50%
export const CAPITAL_STACK = { participant: 0.15, eqtPerf: 0.35, seb: 0.50 };

// Waterfall splits from Excel "Other fund info" sheet
export const WATERFALL = {
  bankRocShare: 0.85,
  bankGainsShare: 0.50,
  empRocShare: 0.15,
  eqtPrefRocShare: 0,
  eqtPrefGainsShare: 0.50,
};

// Fee rates from Excel "Other fund info"
export const FEE_RATES = {
  opex: 0.0012,
  commitmentFee: 0.004,
  arrangementFee: 0.0025,
};

// Fund deployment schedule — 6 years
export const INVESTMENT_PACE = [0.25, 0.25, 0.20, 0.15, 0.05, 0];
export const INVESTMENT_LEVEL = 0.9;

// Fund bridge facility
export const BRIDGE_CAP_PCT = 0.35;

// Defaults
export const DEFAULT_EQT_PERF_RATE = 8.0;
export const DEFAULT_LEVERAGE_RATIO = 6.67;
export const DEFAULT_BASE_RATE = 5.0;
export const DEFAULT_BANK_INTEREST_RATE = 5.57;
export const DEFAULT_PAYOUT_YEARS = 4;
export const DEFAULT_FEDERAL_CAP_GAINS = 20;
export const DEFAULT_STATE_CAP_GAINS = 5;
export const EMPLOYEE_DRAWDOWN_PAYMENTS = 3;
export const REGULAR_DRAWDOWN_PAYMENTS = 2;
export const DEFAULT_HOLDING_PERIOD = 5;

// J-curve model parameters
export const J_CURVE = {
  deployPct: 0.30,
  depth: 0.15,
  recoveryFade: 0.3,
  floor: 0.5,
};

// Performance tracking thresholds
export const PERF_THRESHOLDS = { ahead: 10, behind: -10 };

// Time constants
export const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

// ── Utility Functions ──

/** Build composite key for fund+investor lookups */
export const makeKey = (fund, investor) => `${fund}||${investor}`;

/** Get distribution amount with fallback chain */
export const getDistAmount = (dist) => dist.total_distribution || dist.net_distribution || 0;

/** Compute latest CAS entry per fund||investor from a CAS timeline array */
export const getLatestCasPerFund = (casTimeline) => {
  const latest = {};
  casTimeline.forEach((cas) => {
    const key = `${cas.fund}||${cas.investor}`;
    if (!latest[key] || cas.date > latest[key].date) {
      latest[key] = cas;
    }
  });
  return latest;
};

/** Get drawdowns for a specific fund+investor, sorted chronologically */
export const getFundDrawdowns = (drawdowns, fund, investor) =>
  (drawdowns || [])
    .filter(d => d.fund === fund && d.investor === investor)
    .sort((a, b) => a.date.localeCompare(b.date));

/** Standard TVPI: (NAV + Distributed) / Drawn */
export const calcTvpi = (drawn, nav, distributed) =>
  drawn > 0 ? (nav + distributed) / drawn : 0;

/** Standard DPI: Distributed / Drawn */
export const calcDpi = (drawn, distributed) =>
  drawn > 0 ? distributed / drawn : 0;

/** Compute capital stack amounts for a leveraged fund */
export const calcCapitalStack = (totalExposure, myCommitment, isLev) => ({
  participantAmt: isLev ? totalExposure * CAPITAL_STACK.participant : myCommitment,
  eqtPerfAmt: isLev ? totalExposure * CAPITAL_STACK.eqtPerf : 0,
  sebAmt: isLev ? totalExposure * CAPITAL_STACK.seb : 0,
});

/** Resolve federal capital gains rate for a year, with fallback */
export const getFedRate = (rates, yr, fallbackYr) =>
  (rates[yr] || rates[fallbackYr] || DEFAULT_FEDERAL_CAP_GAINS) / 100;

/** Resolve state capital gains rate for a year, with fallback */
export const getStateRate = (rates, yr, fallbackYr) =>
  (rates[yr] || rates[fallbackYr] || DEFAULT_STATE_CAP_GAINS) / 100;

/**
 * Year-by-year leveraged fund simulation.
 * Returns: { projectedSebInterest, projectedEqtPerfInterest, projectedTotalInterest,
 *            lpNetReturn, sebRepaid, eqtPerfRepaid, participantRepaid,
 *            sebGains, eqtPerfGains, participantGains, simInterestByYear, simDistributionsByYear }
 */
export const simulateLeveragedFund = ({
  totalExposure, sebAmt, eqtPerfRate, bankInterestRate,
  firstInvestmentYear, vintageYear, avgHoldingPeriod,
  effectiveFundMoic, participantAmt,
}) => {
  const simYears = (firstInvestmentYear + INVESTMENT_PACE.length - 1 + avgHoldingPeriod) - firstInvestmentYear + 1;
  const investmentsByYear = [];
  let bankDrawn = 0, eqtPrefDrawn = 0, bridgeDrawn = 0;
  let totalEmpDistributions = 0;
  const bankFacility = sebAmt || (totalExposure * CAPITAL_STACK.seb);
  const bridgeCap = totalExposure * BRIDGE_CAP_PCT;
  let cumulativeBankUtilized = 0;
  let arrangementFeePaid = false;
  const equityCommitment = participantAmt;
  const empDrawdownPerYear = equityCommitment / EMPLOYEE_DRAWDOWN_PAYMENTS;
  const empDrawdownStart = vintageYear;
  const empDrawdownEnd = empDrawdownStart + 2;

  let projectedSebInterest = 0, projectedEqtPerfInterest = 0;
  let sebRepaid = 0, eqtPerfRepaid = 0, participantRepaid = 0;
  let sebGains = 0, eqtPerfGains = 0, participantGains = 0;
  const simInterestByYear = {};
  const simDistributionsByYear = {};

  for (let i = 0; i < simYears; i++) {
    const yr = firstInvestmentYear + i;
    const pace = i < INVESTMENT_PACE.length ? INVESTMENT_PACE[i] : 0;
    const newInvestment = totalExposure * pace;
    investmentsByYear.push(newInvestment);

    let fundRoC = 0, fundCapGains = 0;
    if (i >= avgHoldingPeriod) {
      const returnIdx = i - avgHoldingPeriod;
      if (returnIdx < investmentsByYear.length && investmentsByYear[returnIdx] > 0) {
        const inv = investmentsByYear[returnIdx];
        fundRoC = inv * Math.min(effectiveFundMoic, 1);
        fundCapGains = inv * Math.max(0, effectiveFundMoic - 1);
      }
    }

    const yearBankInterest = bankDrawn * bankInterestRate;
    const undrawnFacility = Math.max(0, bankFacility - cumulativeBankUtilized);
    const yearCommitmentFee = (bankDrawn > 0 || cumulativeBankUtilized > 0) ? undrawnFacility * FEE_RATES.commitmentFee : 0;
    const yearArrangementFee = (!arrangementFeePaid && cumulativeBankUtilized > 0) ? bankFacility * FEE_RATES.arrangementFee : 0;
    if (yearArrangementFee > 0) arrangementFeePaid = true;
    const yearOpex = (bankDrawn > 0 || newInvestment > 0 || eqtPrefDrawn > 0 || bridgeDrawn > 0)
      ? totalExposure * FEE_RATES.opex : 0;
    const yearTotalCosts = yearBankInterest + yearCommitmentFee + yearArrangementFee + yearOpex;
    projectedSebInterest += yearBankInterest;
    const totalUses = newInvestment + yearTotalCosts;

    let yearBankDraw = 0, yearEmpDraw = 0, yearEqtPrefDraw = 0;
    let yearBridgeDraw = 0, yearBridgeRepay = 0;
    if (yr < empDrawdownStart) {
      yearBridgeDraw = Math.min(newInvestment, bridgeCap - bridgeDrawn);
      yearEqtPrefDraw = yearTotalCosts;
    } else {
      yearBankDraw = newInvestment * CAPITAL_STACK.seb + yearTotalCosts * CAPITAL_STACK.seb / (CAPITAL_STACK.seb + CAPITAL_STACK.eqtPerf);
      yearEmpDraw = (yr >= empDrawdownStart && yr <= empDrawdownEnd) ? empDrawdownPerYear : 0;
      yearBridgeRepay = Math.min(bridgeDrawn, yearBankDraw + yearEmpDraw);
      yearEqtPrefDraw = Math.max(0, totalUses - yearBankDraw - yearEmpDraw + yearBridgeRepay);
      cumulativeBankUtilized += yearBankDraw;
    }
    bridgeDrawn = Math.max(0, bridgeDrawn + yearBridgeDraw - yearBridgeRepay);
    bankDrawn += yearBankDraw;
    eqtPrefDrawn += yearEqtPrefDraw;

    const bankRocRepay = Math.min(bankDrawn, fundRoC * WATERFALL.bankRocShare);
    const bankGainsRepay = Math.min(Math.max(0, bankDrawn - bankRocRepay), fundCapGains * WATERFALL.bankGainsShare);
    bankDrawn = Math.max(0, bankDrawn - bankRocRepay - bankGainsRepay);
    sebRepaid += bankRocRepay + bankGainsRepay;

    const eqtPrefGainsRepay = Math.min(eqtPrefDrawn, fundCapGains * WATERFALL.eqtPrefGainsShare);
    eqtPrefDrawn = Math.max(0, eqtPrefDrawn - eqtPrefGainsRepay);
    eqtPerfRepaid += eqtPrefGainsRepay;
    const yearEqtPrefInterest = eqtPrefDrawn > 0 ? eqtPrefDrawn * eqtPerfRate / 100 : 0;
    projectedEqtPerfInterest += yearEqtPrefInterest;
    eqtPrefDrawn += yearEqtPrefInterest;

    simInterestByYear[yr] = {
      sebInterest: yearBankInterest,
      eqtPerfInterest: yearEqtPrefInterest,
      total: yearBankInterest + yearEqtPrefInterest + yearCommitmentFee + yearArrangementFee + yearOpex,
    };

    const empRoc = fundRoC * WATERFALL.empRocShare;
    const totalAllocated = empRoc + bankRocRepay + 0 + bankGainsRepay + eqtPrefGainsRepay;
    const residual = Math.max(0, (fundRoC + fundCapGains) - totalAllocated);
    const yearEmpDist = empRoc + residual;
    totalEmpDistributions += yearEmpDist;
    if (yearEmpDist > 0) {
      simDistributionsByYear[yr] = yearEmpDist;
      participantRepaid += Math.min(yearEmpDist, Math.max(0, participantAmt - participantRepaid));
      participantGains += Math.max(0, yearEmpDist - Math.max(0, participantAmt - participantRepaid + participantGains));
    }
  }

  const projectedTotalInterest = projectedSebInterest + projectedEqtPerfInterest;
  const lpNetReturn = Math.max(0, totalEmpDistributions);

  return {
    projectedSebInterest, projectedEqtPerfInterest, projectedTotalInterest,
    lpNetReturn, sebRepaid, eqtPerfRepaid, participantRepaid,
    sebGains, eqtPerfGains, participantGains, simInterestByYear, simDistributionsByYear,
  };
};
